/**
 * Moving reflections on the pools, and highlights falling down the falls.
 *
 * Ported from apps/display/public/water.js. The polygons are traced round the
 * water in the current paintings, as fractions of the painting, and use the
 * same `camera` as the wildlife so a reflection is always on water. Like the
 * resident routes, they belong to these paintings: a repainted stream needs
 * them retraced.
 *
 * Canvas draws 68 landscape or 86 portrait strokes per frame, clipped to
 * irregular pool shapes, without adding a DOM element for every reflection.
 */

import { camera, type Camera } from './wildlife/layout';

type Polygon = ReadonlyArray<readonly [number, number]>;

const WIDE: readonly Polygon[] = [
  [[0.552, 0.591], [0.632, 0.580], [0.654, 0.613], [0.642, 0.639], [0.599, 0.614], [0.572, 0.606]],
  [[0.653, 0.735], [0.724, 0.730], [0.84, 0.783], [0.995, 0.815], [1, 0.856], [0.82, 0.815], [0.727, 0.785], [0.67, 0.760]],
  [[0.745, 0.913], [0.82, 0.922], [0.868, 0.94], [0.93, 0.965], [1, 0.961], [1, 1], [0.739, 1], [0.720, 0.954]],
];

const PORTRAIT: readonly Polygon[] = [
  [[0.58, 0.591], [0.71, 0.585], [0.79, 0.607], [0.759, 0.62], [0.684, 0.611], [0.61, 0.607]],
  [[0.644, 0.644], [0.781, 0.640], [0.935, 0.665], [0.925, 0.682], [0.78, 0.666], [0.693, 0.656]],
  [[0.751, 0.722], [0.9, 0.718], [1, 0.732], [1, 0.765], [0.83, 0.746], [0.744, 0.738]],
  [[0.852, 0.831], [0.94, 0.826], [1, 0.842], [1, 0.878], [0.841, 0.855]],
];

// Only the two short upper cascades remain in the reviewed V20 paintings.
// Former middle/lower falls are now flat pools and must not emit falling foam.
const FALLS_WIDE = [[.599,.558,.023,.005],[.619,.590,.042,.006]] as const;
const FALLS_PORTRAIT = [[.659,.561,.047,.003],[.705,.584,.066,.004]] as const;

/** A cheap, deterministic scatter: the same seed lands in the same place. */
const random = (n: number) => {
  const v = Math.sin(n * 127.1 + 13.7) * 43758.5453;
  return v - Math.floor(v);
};

export class Water {
  private ctx: CanvasRenderingContext2D | null;
  private view: Camera = camera(1, 1);
  private width = 1;
  private height = 1;
  private ratio = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    // Null in a test environment and on the rare browser with canvas off.
    // Every method below tolerates it: the valley simply has still water.
    this.ctx = canvas.getContext('2d');
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    // Capped at 2. A 3x phone would triple the fill cost for strokes nobody
    // can resolve at that density.
    this.ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(width * this.ratio);
    this.canvas.height = Math.round(height * this.ratio);
    this.view = camera(width, height);
  }

  render(seconds: number, life: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const { view } = this;
    ctx.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    if (life <= 0) return; // a barren valley's stream is dry
    ctx.translate(view.x, view.y);
    const scale = view.height / (view.portrait ? 1672 : 941);

    (view.portrait ? PORTRAIT : WIDE).forEach((polygon, pool) => {
      const xs = polygon.map((p) => p[0] * view.width);
      const ys = polygon.map((p) => p[1] * view.height);
      const left = Math.min(...xs);
      const top = Math.min(...ys);
      const w = Math.max(...xs) - left;
      const h = Math.max(...ys) - top;

      ctx.save();
      ctx.beginPath();
      polygon.forEach(([x, y], i) =>
        i ? ctx.lineTo(x * view.width, y * view.height) : ctx.moveTo(x * view.width, y * view.height));
      ctx.closePath();
      ctx.clip();
      for (let i = 0; i < 18; i++) {
        const seed = i + pool * 97;
        const phase = (random(seed) + seconds * (0.06 + pool * 0.012)) % 1;
        const x = left + random(seed + 42) * w + Math.sin(seconds * 0.4 + seed) * 4*scale;
        const y = top + phase * h;
        const length = (5 + random(seed + 86) * 15) * scale;
        const alpha = Math.sin(phase * Math.PI) * (0.08 + random(seed + 29) * 0.10) * life;
        ctx.strokeStyle = `rgba(238,255,242,${alpha})`;
        ctx.lineWidth = (0.6 + random(seed + 5) * 0.8) * scale;
        ctx.beginPath();
        ctx.moveTo(x - length, y);
        ctx.quadraticCurveTo(x, y + Math.sin(seconds * 1.5 + seed) * 2*scale, x + length, y - scale);
        ctx.stroke();
      }
      ctx.restore();
    });

    // Waterfall highlights fall straight down while the pools drift below.
    for (const [cx, cy, spread, drop] of view.portrait ? FALLS_PORTRAIT : FALLS_WIDE) {
      for (let i = 0; i < 7; i++) {
        const seed=i+Math.round(cx*91+cy*137);
        const phase = (random(seed) + seconds * 0.8) % 1;
        const x = (cx + (random(seed + 5) - 0.5) * spread) * view.width;
        const y = (cy + phase * drop) * view.height;
        ctx.strokeStyle = `rgba(249,255,244,${Math.sin(phase * Math.PI) * 0.18 * life})`;
        ctx.lineWidth = 1.1*scale;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(x + scale, y + 3*scale, x + 3*scale, y + 8 * scale);
        ctx.stroke();
      }
    }
  }
}
